// frontend/src/pages/portal/modifiers/ProductOptionsTab.js
//
// Manager Portal — "Options" tab inside a single product's edit modal
// (PortalProductsPage.js's ProductModal). This is the other side of Task 10's
// Option Groups tab: instead of building a group, a tenant picks which
// already-built groups (Flavour, Extras, ...) apply to *this* product.
//
// Visual contract: docs/mockups/nested-addons/portal-group-builder.html,
// the "On a product" view (renderProd()).
//
// Auto-save, no separate Save button (portal list-op convention, same as
// ModifierGroupEditor's option add/remove): every attach or detach calls
// modifierService.setProductGroups(productCode, groupIds) immediately.
//
// `attachedGroupIds` is trusted as the full, current source of truth — the
// caller is responsible for loading it (via modifierService.getProductGroups)
// before rendering this tab with interaction enabled, since every save here
// replaces the product's *entire* attachment list (mirroring the backend's
// delete-then-reinsert PUT /modifiers/products/{code}/groups). Rendering this
// with a stale or empty attachedGroupIds and letting the tenant attach one
// group would silently wipe out every other group already on the product.
import React, { useState, useEffect, useMemo } from 'react';
import { C, FONT } from '../../../styles/theme';
import { modifierService } from '../../../services/api';
import { ruleLabel, isRequired } from './modifierRules';

const rowStyle = {
    display: 'flex', alignItems: 'center', gap: 11, padding: '12px 13px',
    border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 8, background: C.white,
};

function AttachedRow({ group, onRemove, t }) {
    const options = group.options || [];
    return (
        <div style={rowStyle}>
            <span style={{ color: C.mutedLight, fontSize: 15, letterSpacing: -2, cursor: 'grab' }}>⋮⋮</span>
            <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: C.text, display: 'block' }}>{group.name}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.muted }}>
                    {ruleLabel(group, t)} · {options.length} {t('options', {}, 'options')}
                </span>
            </span>
            <span style={{
                fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase',
                borderRadius: 6, padding: '3px 8px',
                color: isRequired(group) ? C.red : C.muted,
                background: isRequired(group) ? C.redLight : C.bg,
                border: `1px solid ${isRequired(group) ? C.redBorder : C.border}`,
            }}>
                {isRequired(group) ? t('required', {}, 'Required') : t('optional', {}, 'Optional')}
            </span>
            <button
                type="button"
                onClick={() => onRemove(group.group_id)}
                aria-label={t('remove', {}, 'Remove')}
                style={{
                    width: 28, height: 28, borderRadius: 8, border: 'none', background: 'none',
                    color: C.mutedLight, cursor: 'pointer', fontSize: 15, display: 'grid', placeItems: 'center',
                }}
            >✕</button>
        </div>
    );
}

function AttachGroupPicker({ open, candidates, onPick, onClose, t }) {
    useEffect(() => {
        const onKey = e => { if (e.key === 'Escape') onClose && onClose(); };
        if (open) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            style={{
                position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.42)', zIndex: 1100,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={onClose}
        >
            <div
                className="portal-modal-card"
                style={{
                    background: C.white, borderRadius: 15, width: 'min(440px, calc(100vw - 40px))',
                    maxHeight: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column',
                    overflow: 'hidden', fontFamily: FONT, boxShadow: '0 24px 70px rgba(0,0,0,0.24)',
                }}
                onClick={e => e.stopPropagation()}
            >
                <div style={{
                    padding: '18px 22px 14px', borderBottom: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', gap: 12,
                }}>
                    <h3 style={{ fontSize: 16, fontWeight: 800, flex: 1, color: C.text, margin: 0 }}>
                        {t('attach_existing_group', {}, 'Attach an existing group')}
                    </h3>
                    <button
                        onClick={onClose}
                        aria-label={t('close', {}, 'Close')}
                        style={{
                            width: 30, height: 30, borderRadius: 9, background: C.bg, border: `1px solid ${C.border}`,
                            color: C.muted, display: 'grid', placeItems: 'center', fontSize: 15, cursor: 'pointer',
                        }}
                    >✕</button>
                </div>

                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 22px' }}>
                    {candidates.length === 0 ? (
                        <div style={{ fontSize: 13, color: C.mutedLight, textAlign: 'center', padding: '20px 0' }}>
                            {t('no_candidate_groups_to_attach', {}, 'Every group you have is already attached — build a new one on the Option Groups tab.')}
                        </div>
                    ) : candidates.map(g => (
                        <button
                            key={g.group_id}
                            type="button"
                            onClick={() => onPick(g)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px',
                                border: `1.5px solid ${C.border}`, borderRadius: 10, marginBottom: 7,
                                fontSize: 13, fontWeight: 700, width: '100%', textAlign: 'left',
                                background: C.white, color: C.text, cursor: 'pointer',
                            }}
                        >
                            <span>{g.name}</span>
                            <span style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 700, color: C.mutedLight }}>
                                {ruleLabel(g, t)}
                            </span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function PreviewChips({ group, addonMap, currency, t }) {
    const options = group.options || [];
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: C.muted, marginBottom: 6 }}>
                {group.name} · {isRequired(group) ? t('required', {}, 'required') : t('optional', {}, 'optional')}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {options.length === 0 ? (
                    <span style={{ fontSize: 11.5, color: C.mutedLight }}>{t('no_options_yet', {}, 'No options yet')}</span>
                ) : options.map(o => {
                    const addon = addonMap[o.addon_product_code];
                    return (
                        <span key={o.addon_product_code} style={{
                            fontSize: 11.5, fontWeight: 600, borderRadius: 999, padding: '3px 10px',
                            color: C.muted, background: C.bg, border: `1px solid ${C.border}`,
                        }}>
                            {addon ? addon.name : o.addon_product_code}
                            {addon && addon.price ? ` ${currency}${Number(addon.price).toFixed(2)}` : ''}
                        </span>
                    );
                })}
            </div>
        </div>
    );
}

export default function ProductOptionsTab({
    productCode, attachedGroupIds = [], allGroups = [], addons = [], currency = '',
    onSaved, t, toast,
}) {
    // Local, ordered list of attached group ids — seeded from the prop and
    // kept in sync if the caller ever swaps products under us (a fresh
    // productCode/attachedGroupIds pair, e.g. switching which product's
    // modal is open) without a full remount.
    const [attached, setAttached] = useState(attachedGroupIds);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setAttached(attachedGroupIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productCode, JSON.stringify(attachedGroupIds)]);

    const groupsById = useMemo(() => {
        const m = {};
        (allGroups || []).forEach(g => { m[g.group_id] = g; });
        return m;
    }, [allGroups]);

    const attachedGroups = useMemo(
        () => attached.map(id => groupsById[id]).filter(Boolean),
        [attached, groupsById],
    );

    const candidates = useMemo(
        () => (allGroups || []).filter(g => !attached.includes(g.group_id)),
        [allGroups, attached],
    );

    const addonMap = useMemo(() => {
        const m = {};
        (addons || []).forEach(a => { m[a.product_code] = a; });
        return m;
    }, [addons]);

    const persist = async (nextIds) => {
        if (!productCode) return;
        setSaving(true);
        try {
            await modifierService.setProductGroups(productCode, nextIds);
            onSaved && onSaved(nextIds);
        } catch {
            toast && toast(t('failed_to_save_options', {}, 'Failed to save option groups'), 'error');
            setAttached(attached); // revert local state to the last known-good list
        } finally {
            setSaving(false);
        }
    };

    const handleAttach = (group) => {
        const nextIds = [...attached, group.group_id];
        setAttached(nextIds);
        setPickerOpen(false);
        persist(nextIds);
    };

    const handleRemove = (groupId) => {
        const nextIds = attached.filter(id => id !== groupId);
        setAttached(nextIds);
        persist(nextIds);
    };

    if (!productCode) {
        return (
            <div style={{
                fontFamily: FONT, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12,
                padding: 32, textAlign: 'center', fontSize: 13, color: C.muted,
            }}>
                {t('save_product_first_to_manage_options', {}, 'Save the product first to attach option groups.')}
            </div>
        );
    }

    return (
        <div style={{ fontFamily: FONT }}>
            <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 20 }}>
                <div>
                    <h4 style={{ fontSize: 14, fontWeight: 800, color: C.text, margin: '0 0 2px' }}>
                        {t('option_groups_on_this_product', {}, 'Option groups on this product')}
                    </h4>
                    <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 12 }}>
                        {t('drag_to_set_cashier_order', {}, 'Drag to set the order the cashier sees them in.')}
                    </div>

                    {attachedGroups.length === 0 ? (
                        <div style={{
                            border: `1px dashed ${C.border}`, borderRadius: 12, padding: 28,
                            textAlign: 'center', fontSize: 13, color: C.mutedLight, marginBottom: 10,
                        }}>
                            {t('no_option_groups_attached', {}, 'No option groups attached yet')}
                        </div>
                    ) : (
                        <div style={{ marginBottom: 10 }}>
                            {attachedGroups.map(g => (
                                <AttachedRow key={g.group_id} group={g} onRemove={handleRemove} t={t} />
                            ))}
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={() => setPickerOpen(true)}
                        disabled={saving}
                        style={{
                            width: '100%', padding: '10px 0', borderRadius: 10,
                            border: `1.5px dashed ${C.border}`, background: 'none',
                            color: C.muted, fontSize: 13, fontWeight: 700,
                            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
                        }}
                    >
                        + {t('attach_existing_group', {}, 'Attach an existing group')}
                    </button>

                    <div style={{
                        marginTop: 14, background: C.blueLight, border: `1px solid ${C.blueBorder}`,
                        borderRadius: 10, padding: '11px 13px', fontSize: 12, color: C.text,
                    }}>
                        <b>{t('attach_dont_rebuild_title', {}, 'Attach, don’t rebuild.')}</b>{' '}
                        {t('attach_dont_rebuild_body', {}, 'The tenant never retypes a flavour list here — they pick one that already exists. Editing it on the Groups tab updates every product at once.')}
                    </div>
                </div>

                <div style={{
                    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16,
                }}>
                    <div style={{
                        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6,
                        color: C.muted, marginBottom: 12,
                    }}>
                        {t('live_preview_cashier', {}, 'Live preview — what the cashier sees')}
                    </div>
                    {attachedGroups.length === 0 ? (
                        <div style={{ fontSize: 12, color: C.mutedLight }}>
                            {t('preview_empty_hint', {}, 'Attach a group to see it here.')}
                        </div>
                    ) : attachedGroups.map(g => (
                        <PreviewChips key={g.group_id} group={g} addonMap={addonMap} currency={currency} t={t} />
                    ))}
                </div>
            </div>

            <AttachGroupPicker
                open={pickerOpen}
                candidates={candidates}
                onPick={handleAttach}
                onClose={() => setPickerOpen(false)}
                t={t}
            />
        </div>
    );
}
